/**
 * One line that says what is happening to the person's work right now
 * (UX 1.0a, UI-alus §1 "Laadimine", "Vead", "Õnnestumine").
 *
 * Always text + icon — never colour alone (WCAG 1.4.1). The `role="status"`
 * region stays mounted across state changes so a screen reader announces each
 * new state, not just the first. `failed` and `stale` offer "Proovi uuesti"
 * when the caller can retry.
 *
 * The default texts are generic; pass `message` for a specific one
 * ("3 tegevust ootab saatmist"). Messages arrive translated.
 */

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HistoryIcon from '@mui/icons-material/History';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

export type StatusLineState = 'working' | 'saving' | 'saved' | 'queued' | 'failed' | 'stale';

export interface StatusLineProps {
  state: StatusLineState;
  /** Translated override for the state's default text. */
  message?: string;
  /** Translated second line: what happens next / what to do. */
  detail?: string;
  /** Shown for `failed` and `stale`. */
  onRetry?: () => void;
}

const DEFAULTS: Record<StatusLineState, string> = {
  working: 'Töötlen…',
  saving: 'Salvestan…',
  saved: 'Salvestatud',
  queued: 'Salvestatud seadmesse — saadetakse, kui ühendus taastub',
  failed: 'Ei õnnestunud',
  stale: 'Andmed võivad olla aegunud',
};

const TONE: Record<StatusLineState, 'text.secondary' | 'success.main' | 'warning.main' | 'error.main' | 'info.main'> = {
  working: 'text.secondary',
  saving: 'text.secondary',
  saved: 'success.main',
  queued: 'info.main',
  failed: 'error.main',
  stale: 'warning.main',
};

function StateIcon({ state }: { state: StatusLineState }) {
  const sx = { fontSize: 20, color: TONE[state] } as const;
  switch (state) {
    case 'working':
    case 'saving':
      return <CircularProgress size={18} aria-hidden sx={{ color: TONE[state] }} />;
    case 'saved':
      return <CheckCircleOutlineIcon aria-hidden sx={sx} />;
    case 'queued':
      return <CloudQueueIcon aria-hidden sx={sx} />;
    case 'failed':
      return <ErrorOutlineIcon aria-hidden sx={sx} />;
    case 'stale':
      return <HistoryIcon aria-hidden sx={sx} />;
  }
}

export function StatusLine({ state, message, detail, onRetry }: StatusLineProps) {
  const { t } = useTranslation();
  const text = message ?? t(`common:ui.statusLine.${state}`, { defaultValue: DEFAULTS[state] });
  const canRetry = Boolean(onRetry) && (state === 'failed' || state === 'stale');

  return (
    <Box role="status" data-state={state} sx={{ py: 0.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
          <StateIcon state={state} />
        </Box>
        <Typography variant="body2" component="span" sx={{ color: 'text.primary', fontWeight: 500 }}>
          {text}
        </Typography>
        {canRetry && (
          <Button size="small" onClick={onRetry} sx={{ ml: 'auto' }}>
            {t('common:ui.statusLine.retry', { defaultValue: 'Proovi uuesti' })}
          </Button>
        )}
      </Stack>
      {detail && (
        <Typography variant="body2" color="text.secondary" sx={{ pl: 3.5 }}>
          {detail}
        </Typography>
      )}
    </Box>
  );
}
